<template>
    <v-card class="forecast-profile-add-edit-container">
        <v-toolbar dark color="primary">
            <v-btn icon dark @click="cancel">
                <v-icon large>close</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-toolbar-title v-if="forecastProfile.BerriesForecastProfileID != null">Edit Forecast Profile</v-toolbar-title>
            <v-toolbar-title v-else>Add New Forecast Profile</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
            </v-toolbar-items>
        </v-toolbar>
        <v-layout class="dialog-content">
            <v-flex xs12 lg8 offset-lg2 pa-2>
                <v-alert v-if="showOfflineWarning" type="warning" :value="true">
                    <span>Warning: Profiles cannot be added, edited or deleted while offline.</span>
                </v-alert>
                <v-card v-if="selectableGlobalProfiles.length > 0" class="mb-1">
                    <v-toolbar dark color="primary" card @click="globalProfilesExpanded = !globalProfilesExpanded">
                        <span class="headline">Apply Template Profile</span>
                        <v-spacer></v-spacer>
                        <v-toolbar-items>
                            <v-btn icon>
                                <v-icon v-if="!globalProfilesExpanded">expand_more</v-icon>
                                <v-icon v-if="globalProfilesExpanded">expand_less</v-icon>
                            </v-btn>
                        </v-toolbar-items>
                    </v-toolbar>
                    <v-card-text v-if="globalProfilesExpanded">
                        <v-form ref="globalProfileForm" v-model="globalProfileFormValid">
                            <v-layout wrap>
                                <v-flex xs12>
                                    <v-autocomplete
                                        v-model="selectedGlobalProfileID"
                                        :items="selectableGlobalProfiles"
                                        label="Template Profile"
                                        clearable
                                        :rules="[rules.requiredObject]">
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex xs12>
                                    <v-btn color="success" :disabled="disableApply" @click="apply">Apply</v-btn>
                                </v-flex>
                            </v-layout>
                        </v-form>
                    </v-card-text>
                    <v-dialog :value="showConfirmApplyDialog" max-width="290">
                        <v-card>
                            <v-toolbar>
                                <v-toolbar-title>Confirm Apply</v-toolbar-title>
                            </v-toolbar>
                            <v-card-text>
                                <span>This action will replace the data in the current crop profile.</span>
                                <span class="mt-1">Are you sure you want to apply this template profile?</span>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn @click="cancelApply">Cancel</v-btn>
                                <v-btn color="success" @click="confirmApply">Apply</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-card>
                <v-alert v-else-if="availableGlobalProfiles != null" type="info" :value="true">
                    <span>There are no template profiles available for the available varieties.</span>
                </v-alert>
                <v-form ref="form" v-model="formValid">
                    <v-layout wrap>
                        <v-flex xs12 v-if="forecastProfile.CropLocationID == null">
                            <v-autocomplete v-if="forecastProfile.BerriesForecastProfileID != null" ref="division"
                                :value="internalDivision"
                                @input="updateDivision"
                                :items="availableDivisions"
                                label="Division"                                
                                return-object                                
                                :loading="loadingDivisions"
                                :rules="[
                                    rules.requiredObject
                                ]">
                            </v-autocomplete>
                            <v-autocomplete v-else ref="divisionList"
                                v-model="divisionIDList"
                                :items="availableDivisions"
                                label="Divisions"                                                          
                                multiple
                                clearable
                                :rules="[
                                    rules.requiredNonEmptyArray
                                ]">
                            </v-autocomplete>
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete ref="variety"
                                :value="internalVariety"
                                @input="updateVariety"
                                :items="availableVarieties"
                                :disabled="varietyReadOnly"
                                label="Variety"                                
                                return-object                                
                                :loading="loadingVarieties"
                                :rules="[
                                    rules.requiredObject
                                ]">
                            </v-autocomplete>
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete ref="croppingSystem"
                                :value="internalCroppingSystem"
                                @input="updateCroppingSystem"
                                :items="availableCroppingSystems"
                                label="Cropping System"
                                return-object
                                :rules="[
                                    rules.requiredObject
                                ]">
                            </v-autocomplete>                        
                        </v-flex>
                        <v-flex xs12>
                            <v-autocomplete ref="plantAge"
                                :value="internalPlantAge"
                                @input="updatePlantAge"
                                :items="availablePlantAges"
                                label="Plant Age"
                                return-object
                                :rules="[
                                    rules.requiredObject
                                ]">
                            </v-autocomplete>                        
                        </v-flex>                        
                        <v-flex xs12>
                            <v-autocomplete ref="startWeek"
                                :value="internalStartWeek"
                                @input="updateStartWeek"
                                :items="availableStartingWeeks"
                                label="Starting Week"
                                clearable
                                :rules="[
                                    rules.requiredObject
                                ]"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 v-if="forecastProfile.CropLocationID != null">
                            <v-text-field ref="startYear"
                                :value="internalStartYear"
                                @input="updateStartYear"
                                type="number"
                                label="Starting Year"
                                clearable
                                :rules="[
                                    rules.requiredNumber, 
                                    rules.integerOnly,
                                    rules.fromAToB(1900, 2999)
                                ]">
                            </v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-text-field ref="tonnesPerHectare"
                                :value="internalTonnesPerHectare"
                                @input="updateTonnesPerHectare"
                                type="number"
                                label="Tonnage/Ha"
                                clearable
                                :rules="[
                                    rules.requiredNumber,                                    
                                    rules.fromAToB(0, 999999)
                                ]">
                            </v-text-field>
                        </v-flex>
                    </v-layout>
                    <v-card>
                        <v-toolbar dark color="primary" card @click="profileValuesExpanded = !profileValuesExpanded">
                            <span class="headline">Profile Values (Assigned Total:{{totalProfileValues}}%)</span>
                            <v-spacer></v-spacer>
                            <v-toolbar-items>
                                <v-btn icon>
                                    <v-icon v-if="!profileValuesExpanded">expand_more</v-icon>
                                    <v-icon v-if="profileValuesExpanded">expand_less</v-icon>
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>
                        <v-card-text v-if="profileValuesExpanded">
                            <v-layout row wrap v-if="weeks">
                                <v-flex xs12>
                                    <v-alert :value="weeksInValid"
                                        outline
                                        type="error">
                                        Week values need to total 100%
                                    </v-alert>
                                </v-flex>
                                <v-flex xs12 sm4 md3 lg3 v-for="(week, i) in weeks" :key="`week${i}`" pa-1>
                                    <v-card>
                                        <v-toolbar dark color="primary" card>
                                            <span class="title">{{ week.label }}</span>
                                        </v-toolbar>
                                        <v-layout column pa-2>
                                            <v-flex>
                                                <v-text-field
                                                    :value="week.value"
                                                    @input="updateWeekValue(i + 1, $event)"
                                                    type="number"
                                                    label="Budget Value (%)"
                                                    :rules="[validateBudgetValue]"
                                                    clearable>
                                                </v-text-field>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                                </v-flex>
                            </v-layout>
                        </v-card-text>
                    </v-card>
                </v-form>
                
                <v-spacer></v-spacer>

                <v-layout wrap v-if="!this.forecastProfile.CropLocationID">
                    <v-flex xs12>
                        <v-card>
                            <v-toolbar dark color="primary" card @click="templatedProfilesExpanded = !templatedProfilesExpanded">
                                <span class="headline">Profiles Using this Template</span>
                                <v-spacer></v-spacer>
                                <v-toolbar-items>
                                    <v-btn icon>
                                        <v-icon v-if="!templatedProfilesExpanded">expand_more</v-icon>
                                        <v-icon v-if="templatedProfilesExpanded">expand_less</v-icon>
                                    </v-btn>
                                </v-toolbar-items>
                            </v-toolbar>
                            <v-card-text v-if="templatedProfilesExpanded">
                                <v-layout row wrap>
                                    <v-flex xs12 v-if="loadingTemplatedProfiles">
                                        <loading></loading>
                                    </v-flex>
                                    <v-flex xs12 v-else-if="loadingTemplatedProfilesError">
                                        <v-alert
                                            :value="true"
                                            outline
                                            type="error">
                                            {{this.loadingTemplatedProfilesError}}
                                        </v-alert>
                                    </v-flex>
                                    <v-flex xs12 v-else-if="!templatedProfiles || !templatedProfiles.TemplatedProfiles || templatedProfiles.TemplatedProfiles.length <= 0">
                                        <v-alert
                                            :value="true"
                                            outline
                                            type="info">
                                            No profiles using this template found
                                        </v-alert>
                                    </v-flex>
                                    <v-flex v-else>
                                        <v-layout row wrap>
                                            <v-flex xs12>
                                                <v-checkbox
                                                    class="center-checkbox"
                                                    v-model="updateTemplatedProfilesAll">
                                                    <div slot="label">
                                                        Update all Profiles using this Template
                                                        <v-tooltip bottom>
                                                            <template v-slot:activator="{ on }">
                                                                <v-icon v-on="on">info</v-icon>
                                                            </template>
                                                            <span>Only crops currently active or active within 30 days of today are shown.</span>
                                                        </v-tooltip>
                                                    </div>
                                                </v-checkbox>
                                            </v-flex>
                                            <v-flex xs12>
                                                <v-data-table
                                                    :items="templatedProfiles.TemplatedProfiles"
                                                    :headers="headers"
                                                    must-sort>
                                                    <template v-slot:items="props">
                                                        <tr>
                                                            <td>{{props.item.CropCode}}</td>
                                                            <td>{{props.item.LocationName}}</td>
                                                            <td>{{props.item.CropStartDate | formatDate}}</td>
                                                            <td>{{props.item.CropEndDate | formatDate}}</td>
                                                            <td>
                                                                <v-checkbox
                                                                    v-model="updateTemplatedProfileIDs"
                                                                    :value="props.item.BerriesForecastProfileID"
                                                                    class="center-checkbox-vertical"
                                                                    label="Update"
                                                                    :disabled="updateTemplatedProfilesAll">
                                                                </v-checkbox>
                                                            </td>
                                                        </tr>
                                                    </template>
                                                </v-data-table>
                                            </v-flex>
                                        </v-layout>
                                    </v-flex>
                                </v-layout>
                            </v-card-text>
                        </v-card>
                    </v-flex>
                </v-layout>

                <v-spacer></v-spacer>

                <v-layout wrap>
                    <v-flex xs12>
                         <v-alert :value="error"
                            outline
                            type="error">
                            {{error}}
                        </v-alert>
                    </v-flex>                    
                    <v-flex xs12 sm6 :pr-1="$vuetify.breakpoint.smAndUp">
                        <v-btn block color="error" @click="cancel">Cancel</v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 :pl-1="$vuetify.breakpoint.smAndUp">
                        <v-btn block color="success" :disabled="disableSave" :loading="loading" @click="save">Save</v-btn>
                    </v-flex>
                </v-layout>                      
            </v-flex>
        </v-layout>
    </v-card>
</template>

<script> 
import Loading from '@/components/generic/Loading.vue';
import validationRules from "@/helpers/validationrules";
import UtilService from '@/services/utilService.js';
import forecastingUtilService from '@/services/forecastingUtilService';
import forecastingProfileApi from '@/api/forecastingProfileApi';
import moment from 'moment';

export default {
    components: {
        Loading
    },
    props: [
        'financialYearCalendars',
        'forecastProfile', /* the forecast profile that is being edited */
        'availableGlobalProfiles', /* the global profiles available to be applied */
        'loading',
        'error',
        'showOfflineWarning',
        'showEdit'
    ],
    data() {
        return {
            rules: validationRules.rules,
            globalProfilesExpanded: true,
            globalProfileFormValid: false,
            selectedGlobalProfileID: null,
            showConfirmApplyDialog: false,
            formValid: false,
            loadingDivisions: false,
            loadingVarieties: false,
            profileValuesExpanded: true,
            loadingTemplatedProfiles: false,
            loadingTemplatedProfilesError: null,
            templatedProfilesExpanded: true,
            templatedProfiles: null,
            updateTemplatedProfileIDs: [],
            updateTemplatedProfilesAll: true,
            headers: [
                {
                    text: "Crop Code",
                    value: "CropCode"
                },
                {   text: "Location", 
                    value: "Location"
                },
                {   text: "Crop Start Date", 
                    value: "CropStartDate"
                },
                {   text: "Crop End Date", 
                    value: "CropEndDate"
                },
                {
                    text: "Update this Crop Profile",
                    sortable: false
                }
            ],
            divisionIDList: [],
        }
    },
    watch: {
        updateTemplatedProfilesAll: {
            handler(newVal, oldVal) {
                if(newVal != oldVal) {
                    if (newVal) {
                        this.selectAllProfilesForUpdate();
                    }
                }
            }   
        },
        showEdit: {
            handler(newVal) {
                if(newVal == true) {
                    this.divisionIDList = [];
                }
            }   
        }
    },
    computed: {
        selectableGlobalProfiles(){
            if(this.availableGlobalProfiles == null){
                return [];
            }

            // Can only select global profiles for the available varieties
            return this.availableGlobalProfiles                
                .map(x => {
                    return {
                        text: `${x.Variety.VarietyName} (${x.Variety.VarietyDescription}, ${x.CroppingSystem != null ? x.CroppingSystem.Description.concat(',') : ''} ${x.PlantAge != null ? x.PlantAge.Description.concat(',') : ''} starting week ${x.StartWeek})`,
                        value: x.BerriesForecastProfileID
                    };
                });
        },
        availableStartingWeeks() {
            let weeks = [];

            for (let index = 1; index <= 52; index++) {
                weeks.push({
                    text: `Week ${index}`,
                    value: index
                });
            }

            return weeks;
        },
        varietyReadOnly() {
            return this.forecastProfile.BerriesForecastProfileID > 0 && this.forecastProfile.CropLocationID != null;
        },
        disableApply(){
            return !this.globalProfileFormValid;
        },
        internalDivision(){
            if(this.forecastProfile.Division == null){
                return null;
            }

            return {
                text: this.forecastProfile.Division.DivisionName,
                value: this.forecastProfile.Division.DivisionID
            };
        },
        internalVariety(){
            if(this.forecastProfile.Variety == null){
                return null;
            }

            return {
                text: `${this.forecastProfile.Variety.VarietyName} (${this.forecastProfile.Variety.VarietyDescription})`,
                value: this.forecastProfile.Variety.VarietyID
            };
        },        
        internalCroppingSystem(){
            if(this.forecastProfile.CroppingSystem == null){
                return null;
            }

            return {
                text: this.forecastProfile.CroppingSystem.Description,
                value: this.forecastProfile.CroppingSystem.BerriesCroppingSystemID
            };
        },
        internalPlantAge(){
            if(this.forecastProfile.PlantAge == null){
                return null;
            }

            return {
                text: this.forecastProfile.PlantAge.Description,
                value: this.forecastProfile.PlantAge.BerriesPlantAgeID
            };
        },
        internalStartWeek(){
            if(this.forecastProfile.StartWeek == null){
                return null;
            }

            return this.forecastProfile.StartWeek;
        },
        internalStartYear(){
            if(this.forecastProfile.StartYear == null){
                return null;
            }

            return this.forecastProfile.StartYear;
        },
        internalTonnesPerHectare(){
            if(this.forecastProfile.TonnesPerHectare == null){
                return null;
            }

            return this.forecastProfile.TonnesPerHectare;
        },
        availableDivisions(){
            let divisions = this.$store.getters["ForecastingProfileStore/getDivisions"];

            divisions = divisions.map(x => {
                return {
                    text: x.DivisionName,
                    value: x.DivisionID
                }
            });

            /* eslint-disable-next-line */
            return divisions.sort(UtilService.sortAscending(x => x.text));
        },
        availableVarieties(){
            let varietySummaries = this.$store.getters["ForecastingProfileStore/getVarietySummaries"];

            varietySummaries = varietySummaries.map(x => {
                return {
                    text: `${x.VarietyName} (${x.VarietyDescription})`,
                    value: x.VarietyID
                }
            });

            /* eslint-disable-next-line */
            return varietySummaries.sort(UtilService.sortAscending(x => x.text));
        },
        availableCroppingSystems() {
            let croppingSystems = this.$store.getters["ForecastingProfileStore/getCroppingSystems"];

            croppingSystems = croppingSystems.map(x => {
                return {
                    text: x.Description,
                    value: x.BerriesCroppingSystemID
                }
            });

            return croppingSystems.sort(UtilService.sortAscending(x => x.text));
        },
        availablePlantAges() {
            let plantAges = this.$store.getters["ForecastingProfileStore/getPlantAges"];

            plantAges = plantAges.map(x => {
                return {
                    text: x.Description,
                    value: x.BerriesPlantAgeID
                }
            });

            return plantAges.sort(UtilService.sortAscending(x => x.text));
        },
        weeks(){
            let weeks = [];

            for(let i = 0; i < 52; i++){
                let week = {
                    label: this.getWeekLabel(i),
                    value: null
                };

                let existingValue = this.forecastProfile.BudgetValues.find(
                    x => x.WeekNumber == (i + 1)
                );
                if(existingValue){
                    week.value = this.formatValue(existingValue.BudgetValue);
                }

                weeks.push(week);
            }

            return weeks;
        },
        weeksInValid() {
            //tkid=2325203 use the computed because rounding with maps and reduces is BORKED
            return this.weeks.every(x => x.value === null) || this.totalProfileValues != 100;
        },
        disableSave(){
            return !this.formValid || this.weeksInValid || this.loadingTemplatedProfiles || this.loadingTemplatedProfilesError;
        },
        totalProfileValues() {
            //tkid=2325203 rounding errors - multiply values by 10, round, sum, then divide
            // ensure we dont get any stray rounding errors outside of the significant figures
            return this.weeks.map(x => Math.round(x.value*10)).reduce((a, b) => (a + b))/10;
        }
    },
    methods: {
        validateBudgetValue(v) {
            if (v != null && v != undefined && !v.toString().match(/^(\d{0,2}(\.\d{1})?|100(\.0?)?)$/)) {
                return "Please enter a number between 1 and 100";
            }

            return false;
        },
        selectAllProfilesForUpdate() {
            this.updateTemplatedProfileIDs = [...this.templatedProfiles.TemplatedProfiles.map(x => { return x.BerriesForecastProfileID })];
        },
        apply(){
            this.showConfirmApplyDialog = true;
        },
        cancelApply(){
            this.showConfirmApplyDialog = false;
        },
        confirmApply(){
            let globalProfile = this.availableGlobalProfiles.find(x => x.BerriesForecastProfileID == this.selectedGlobalProfileID);

            // Replace forecastProfile fields with globalProfile fields
            this.updatePlant({ value: globalProfile.Plant.PlantID });
            this.updateDivision({ value: globalProfile.Division.DivisionID });
            this.updateVariety({ value: globalProfile.Variety.VarietyID });
            this.updateCroppingSystem({ value: globalProfile.CroppingSystem.BerriesCroppingSystemID });
            this.updatePlantAge({ value: globalProfile.PlantAge.BerriesPlantAgeID });
            this.updateStartWeek(globalProfile.StartWeek);
            this.updateTonnesPerHectare(globalProfile.TonnesPerHectare);            
            
            this.forecastProfile.BudgetValues.splice(0, this.forecastProfile.BudgetValues.length, ...globalProfile.BudgetValues
                .map(x => {
                    return {
                        BerriesForecastProfileID: this.forecastProfile.BerriesForecastProfileID,
                        WeekNumber: x.BerriesForecastProfileWeekNumber,
                        BudgetValue: x.BudgetValue
                    }
                })
            );

            this.updateTemplateBerriesForecastProfileID(this.selectedGlobalProfileID);

            this.cancelApply();
        },
        getWeekLabel(weekIndex){
            let label = `Week ${(weekIndex + 1)}`;

            if (this.forecastProfile.CropLocationID != null && this.forecastProfile.StartYear != null && this.forecastProfile.StartWeek != null) {
                let start = forecastingUtilService.getFinancialStartDateFromProfile(this.forecastProfile, this.financialYearCalendars);

                label += ` (${this.$options.filters.formatDate(moment(start).add(weekIndex, 'week'))})`;
            }

            /*if(this.forecastProfile.StartWeek != null) {
                if (this.forecastProfile.StartWeek + weekIndex > 52) {
                    label = `Week ${(this.forecastProfile.StartWeek + (weekIndex) - 52)}`;
                } else {
                    label = `Week ${this.forecastProfile.StartWeek + weekIndex}`;
                }

                let labelSuffix = "";

                if(this.forecastProfile.StartYear != null && this.forecastProfile.StartYear >= 1000){
                    let year = this.forecastProfile.StartYear;

                    if (this.forecastProfile.StartWeek + weekIndex > 52) {
                        year += 1;
                    }

                    year = year + "";
                    while(year.length < 4){
                        year = "0" + year;
                    }

                    if(labelSuffix.length > 0){
                        labelSuffix += " ";
                    }
                    labelSuffix += `${year}`;
                }

                if(labelSuffix.length > 0){
                    label = `${label} (${labelSuffix})`;
                }
            }*/
            
            return label;
        },
        updatePlant(v) {
            if(v == null){
                this.$set(this.forecastProfile, "Plant", null);
            } else {
                this.$set(this.forecastProfile, "Plant", this.$store.getters["ForecastingProfileStore/getPlants"].find(
                    x => x.PlantID == v.value
                ));
            }
        },
        updateTemplateBerriesForecastProfileID(v) {
            this.$set(this.forecastProfile, "TemplateBerriesForecastProfileID", v);
        },
        updateDivision(v) {
            if(v == null){
                this.$set(this.forecastProfile, "Division", null);
            } else {
                this.$set(this.forecastProfile, "Division", this.$store.getters["ForecastingProfileStore/getDivisions"].find(
                    x => x.DivisionID == v.value
                ));
            }
        },
        updateVariety(v){
            if(v == null){
                this.$set(this.forecastProfile, "Variety", null);
            } else {
                this.$set(this.forecastProfile, "Variety", this.$store.getters["ForecastingProfileStore/getVarietySummaries"].find(
                    x => x.VarietyID == v.value
                ));
            }
        },
        updateCroppingSystem(v) {
            if(v == null){
                this.$set(this.forecastProfile, "CroppingSystem", null);
            } else {
                this.$set(this.forecastProfile, "CroppingSystem", this.$store.getters["ForecastingProfileStore/getCroppingSystems"].find(
                    x => x.BerriesCroppingSystemID == v.value
                ));
            }
        },
        updatePlantAge(v){
            if(v == null){
                this.$set(this.forecastProfile, "PlantAge", null);
            } else {
                this.$set(this.forecastProfile, "PlantAge", this.$store.getters["ForecastingProfileStore/getPlantAges"].find(
                    x => x.BerriesPlantAgeID == v.value
                ));
            }
        },
        updateStartWeek(v){
            this.$set(this.forecastProfile, "StartWeek", parseInt(v));
        },
        updateStartYear(v){
            this.$set(this.forecastProfile, "StartYear", parseInt(v));
        },
        updateTonnesPerHectare(v) {
            this.$set(this.forecastProfile, "TonnesPerHectare", v);
        },
        updateWeekValue(weekNumber, v){
            let existing = this.forecastProfile.BudgetValues.find(
                x => x.WeekNumber == weekNumber
            );
            if(!existing){
                existing = {};
                this.$set(existing, "WeekNumber", weekNumber);
                this.$set(existing, "BudgetValue", null);
                this.forecastProfile.BudgetValues.push(existing);
            }

            this.$set(existing, "BudgetValue", this.parseValue(v));
        },
        parseValue(v){
            if(v == null || v === ""){
                return null;
            }

            // Convert to string
            v = v + "";

            // Convert to valid string
            if(v.indexOf(".") >= 0){
                let split = v.split(".");
                v = split[0] + ".";
                if(split[1].length > 3){
                    v += split[1].substring(0, 3);
                } else {
                    v += split[1];
                }
            }

            v = parseFloat(v) // Convert string to number
            v = v / 100; // Convert to %

            // Handle messy floats
            return Math.round(v * 100000) / 100000;
        },
        formatValue(v){
            if(v == null || v === ""){
                return null;
            }

            v = parseFloat(v) // Convert string to number
            v = v * 100; // Convert from %

            // Handle messy floats
            return Math.round(v * 100000) / 100000 ;
        },
        async init() {
            try {
                this.loadingTemplatedProfiles = true;
                this.loadingTemplatedProfilesError = null;
                this.updateTemplatedProfileIDs = [];
                this.updateTemplatedProfilesAll = true;

                // if the forecast is not attached to a crop, then we know it's a template
                // we only need to check for profiles that *use* this template if we're editing a template
                if (!this.forecastProfile.CropLocationID) {
                    // make a request looking for any profiles that use the current template
                    const response = await forecastingProfileApi.getTemplatedProfiles(this.forecastProfile.BerriesForecastProfileID);

                    this.templatedProfiles = response;

                    this.selectAllProfilesForUpdate();
                }
            }
            catch(error) {
                this.loadingTemplatedProfilesError = error;
            }
            finally {
                this.loadingTemplatedProfiles = false;
            }
        },
        cancel() {
            this.$emit("onCancel");
        },
        save() {
            this.$emit("onSave", {
                updateTemplatedProfileIDs: this.updateTemplatedProfileIDs,
                divisionIDList: this.divisionIDList,
            });
        },
    },
    created() {

    }
}
</script>

<style>
.forecast-profile-add-edit-container .center-checkbox .v-input__control {
    margin-left: auto;
    margin-right: auto;
}

.forecast-profile-add-edit-container .center-checkbox-vertical .v-input__slot {
    margin-top: auto;
    margin-bottom: auto;
}
</style>
